<template>
  <div id="addPage">
    <h3 v-if="$route.query.step !== 'folders'">Select a project</h3>
    <AddToExistingProject :entityType="'ofs_project_item'"
                          :entityBundle="entityBundle"
                          :selectFolderCallback="addToFolder"/>
    <MessageModal v-show="visibility.confirmation"
                  :title="'Success'"
                  :message="'Item sucessfully added to project.'"
                  @close="visibility.confirmation = false"/>
  </div>
</template>

<script>

import sanitizeHtml from 'sanitize-html'
import MessageModal from '@/components/modals/MessageModal.vue'
import AddToExistingProject from '@/components/AddToExistingProject.vue'

definePageMeta({
  layout: 'add',
});

/**
 * A page to show on any site, that will allow a user to add content to a project (or folder).
 *
 * It's a seperate page so that you can iframe to this page and allow cross domain addition to projects.
 *
 * Without recreating "add to project" features on every domain (ofs.com, myofs.com, estimator, etc).
 */
export default {
  name: 'AddPage',
  components: {
    MessageModal,
    AddToExistingProject,
  },
  data () {
    return {
      visibility: {
        confirmation: false,
      },
      entityBundle: this.$route.params.type,
      entityType: 'item',
      entityId: this.$route.params.id,
    }
  },
  computed: {
    ...mapStores(useStore),
  },
  mounted () {
    if (process.client) {
      // Save query saver to session storage to use it later on to save.
      sessionStorage.setItem('query', this.$route.query.query);
      sessionStorage.setItem('file', this.$route.query.file);
      sessionStorage.setItem('callback', this.$route.query.callback);
    }

    let fileSave = null
    if (
      process.client &&
      typeof sessionStorage.getItem('file') !== 'undefined' &&
      sessionStorage.getItem('file') !== 'undefined'
    ) {
      fileSave = sanitizeHtml(sessionStorage.getItem('file'))
    }

    useFetchDrupal('/projects/item-type/' + this.entityBundle + '?file=' + fileSave, {
      method: 'GET'
    }).then((data) => {
      this.itemType = data.item_type
    }).catch(() => {})
  },
  methods: {
    /**
     * Add item to folder.
     */
    addToFolder (project, folder) {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        'event': 'projects',
        'module': 'ofs_projects',
        'action': 'add item',
        'value': this.entityBundle,
        'project': project,
        'folder': folder,
        'item_reference': this.entityId,
        'item_reference_type': this.entityBundle,
      })

      let callbackSave = null
      if (typeof sessionStorage.getItem('callback') !== 'undefined' &&
        sessionStorage.getItem('callback') !== 'undefined') {
        callbackSave = sanitizeHtml(sessionStorage.getItem('callback'))
      }
      let querySave = null
      if (
        typeof sessionStorage.getItem('query') !== 'undefined' &&
        sessionStorage.getItem('query') !== 'undefined'
      ) {
        querySave = sanitizeHtml(sessionStorage.getItem('query'))
      }
      let fileSave = null
      if (
        typeof sessionStorage.getItem('file') !== 'undefined' &&
        sessionStorage.getItem('file') !== 'undefined'
      ) {
        fileSave = sanitizeHtml(sessionStorage.getItem('file'))
      }

      if (callbackSave) {
        let data = JSON.stringify({
          action: callbackSave,
          type: this.entityBundle,
          id: this.entityId,
          query: querySave,
          file: fileSave,
          folder: folder,
          project: project,
        })
        window.parent.postMessage(data, '*')
      } else {
        useFetchDrupal('/projects/item/create', {
          method: 'POST',
          body: {
            folders: [folder],
            item: {
              type: this.entityBundle,
              id: this.entityId,
              query: querySave,
              file: fileSave,
            }
          }
        }).then(() => {
          this.projects.forEach((project) => {
            project.active = false
          })

          // Not dry since this may vary.
          let data = JSON.stringify({
            action: 'myProjectsItemAdded',
            type: this.entityBundle,
            id: this.entityId,
            query: querySave,
            file: fileSave,
            folder: folder,
            project: project,
          })

          window.parent.postMessage(data, '*')
          // Wait half a second.
          new Promise(resolve => setTimeout(() => {
            this.visibility.confirmation = true
          }, 500))
        }).catch(() => {
          this.store.setMessage({
            type: 'error',
            text: 'Error adding item to project, please try again.'
          })
        })
      }
    },
  }
}
</script>

<style lang="scss" scoped>
#addPage {
  padding: 2rem;
}
</style>
